<script setup lang="ts">
import { useCurrencyFormatter } from '~/shared-kernel/presentation/composables/useCurrencyFormatter'
import { formatBusinessDate } from '~/shared-kernel/domain/business-time'

const props = defineProps<{
  quantity: number
  total: { amount: number; currency: string } | null
  expiresAt?: string | undefined
  notice: string | null
}>()

const emit = defineEmits<{ open: [] }>()

const { t, locale } = useI18n()
const { formatMoney } = useCurrencyFormatter()
const detail = computed(() => [
  t('public.purchase.barCount', { count: props.quantity }, props.quantity),
  ...(props.expiresAt ? [t('public.purchase.barUntil', { time: formatBusinessDate(props.expiresAt, locale.value, { timeStyle: 'short' }) })] : [])
].join(' · '))
</script>

<template>
  <div class="public-purchase-bar" :class="{ 'public-purchase-bar--notice': notice }" data-purchase-tour="cart-bar">
    <slot />
    <div class="public-purchase-bar__row">
      <div class="public-purchase-bar__summary" role="status">
        <strong v-if="notice" class="public-purchase-bar__notice"><UIcon name="i-lucide-circle-check" aria-hidden="true" />{{ notice }}</strong>
        <strong v-else-if="quantity === 0">{{ t('public.purchase.cartEmptyTitle') }}</strong>
        <strong v-else>{{ total ? formatMoney(total.amount, total.currency) : '—' }}</strong>
        <span>{{ quantity === 0 ? t('public.purchase.barEmptyHint') : detail }}</span>
      </div>
      <TqButton compact icon="i-lucide-shopping-cart" data-purchase-tour="view-cart" @click="emit('open')">{{ t('public.purchase.viewCart') }}</TqButton>
    </div>
  </div>
</template>

<style scoped>
.public-purchase-bar { position: fixed; z-index: 74; right: 0; bottom: 0; left: 0; display: grid; gap: .6rem; padding: .65rem 1rem calc(.65rem + env(safe-area-inset-bottom)); border-top: 1px solid var(--tq-border); background: var(--tq-bg-elevated); box-shadow: var(--tq-shadow-soft); }
.public-purchase-bar__row { display: flex; align-items: center; justify-content: space-between; gap: .75rem; }
.public-purchase-bar__summary { display: grid; gap: .1rem; min-width: 0; }
.public-purchase-bar__summary strong { overflow: hidden; color: var(--tq-text); font-size: .95rem; text-overflow: ellipsis; white-space: nowrap; }
.public-purchase-bar__summary span { color: var(--tq-text-soft); font-size: .78rem; }
.public-purchase-bar__notice { display: inline-flex; align-items: center; gap: .35rem; }
.public-purchase-bar__notice .iconify { flex-shrink: 0; color: var(--tq-success-readable); }
.public-purchase-bar__row > :last-child { flex-shrink: 0; }
.public-purchase-bar--notice { animation: public-purchase-bar-pulse .6s var(--tq-ease-soft); }
@keyframes public-purchase-bar-pulse {
  40% { box-shadow: 0 -.35rem 1.25rem color-mix(in oklch, var(--tq-action) 35%, transparent); }
}
@media (prefers-reduced-motion: reduce) {
  .public-purchase-bar--notice { animation: none; }
}
</style>
